import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import type { ChangeEvent } from 'react';
import { useId } from 'react';
import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { Button } from '@/shared/ui/button/Button';
import { CloseButton } from '@/shared/ui/button/CloseButton';
import { Text } from '@/shared/ui/text';
import { FormFieldWrapper, type FormFieldWrapperProps } from '../components/FormFieldWrapper';
import type { FormFieldSize } from '../helpers/field-size';

/**
 * FileUploadField コンポーネント
 *
 * FieldItem でラップされた File ArrowUpTrayIcon コンポーネント
 * React Hook Form と統合され、ラベル・バリデーション・エラーメッセージを含む
 *
 * 使用例:
 * ```tsx
 * <FileUploadField
 *   control={form.control}
 *   name="file"
 *   label="CSVファイル"
 *   accept=".csv"
 *   required
 *   onChange={(file) => console.log('File selected:', file)}
 * />
 * ```
 */
interface FileUploadFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<FormFieldWrapperProps<TFieldValues, TName>, 'children'> {
  /** 受け入れるファイルタイプ（例: ".csv", "image/*", ".pdf,.doc,.docx"） */
  accept?: string;
  /** ボタンテキスト */
  buttonText?: string;
  /** フィールドの最小幅（デフォルト: sm） */
  size?: FormFieldSize;
  /** ファイル選択時のコールバック */
  onChange?: (file: File) => void;
}

export const FileUploadField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  className,
  accept,
  buttonText = 'ファイルを選択',
  size = 'sm',
  onChange,
}: FileUploadFieldProps<TFieldValues, TName>) => {
  const inputId = useId();

  return (
    <FormFieldWrapper
      control={control}
      name={name}
      label={label}
      required={required}
      supportText={supportText}
      showSupportText={showSupportText}
      readOnly={readOnly}
      displayValue={displayValue}
      className={className}
      size={size}
    >
      {({
        field,
        fieldId,
        hasError,
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldId: string;
        hasError: boolean;
      }) => {
        const selectedFile = field.value as File | undefined;

        const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
          const file = event.target.files?.[0];
          if (file) {
            field.onChange(file);
            onChange?.(file);
          }
        };

        return (
          <div className="flex items-center gap-4">
            <input
              id={inputId}
              type="file"
              accept={accept}
              onChange={handleFileChange}
              onBlur={field.onBlur}
              name={field.name}
              className="hidden"
              aria-invalid={hasError}
              aria-describedby={hasError ? `${fieldId}-error` : undefined}
            />
            {selectedFile ? (
              <div className="flex items-center gap-2">
                <Text variant="body">{selectedFile.name}</Text>
                <CloseButton
                  aria-label="ファイルを削除"
                  onClick={() => {
                    field.onChange(undefined);
                    // input要素の値もリセット
                    const inputEl = document.getElementById(inputId) as HTMLInputElement | null;
                    if (inputEl) inputEl.value = '';
                  }}
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                data-field={field.name}
                onClick={() => document.getElementById(inputId)?.click()}
              >
                <ArrowUpTrayIcon className="mr-2 h-4 w-4" />
                {buttonText}
              </Button>
            )}
          </div>
        );
      }}
    </FormFieldWrapper>
  );
};

FileUploadField.displayName = 'FileUploadField';
